<template>
  <button
    class="pe-button box-border inline-flex items-center gap-1"
    :disabled="isDisabled"
    :class="{
      ...getBtnClasses(),
      'rounded-full': props.round,
      'rounded-lg': !props.round
    }"
    type="button"
    v-bind="$attrs"
  >
    <span v-if="loading"> 
      <UiIcon icon="loader-2" class="animate-spin" />
      {{ loadingText || $t('feedback.await') }}
    </span>
    <slot v-else />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import UiIcon from '@/components/ui/icon.vue'

const props = defineProps<{
  type?: string
  round?: boolean
  disabled?: boolean
  loading?: boolean
  loadingText?: string
  size?: string
}>()

const getBtnClasses = () => {
  const classes = []
  if (['primary', 'info', 'success', 'warning', 'danger', 'white', 'secondary'].includes(props.type)) classes[props.type] = true
  if (['small', 'large'].includes(props.size)) classes[`--pe-button-${props.size}`] = true

  return classes
}

const isDisabled = computed(() => props.disabled || props.loading)
</script>

<style lang="scss" scoped>
.pe-button {
  text-transform: inherit;
  @apply border border-neutral-900/10 bg-neutral-100 px-4 py-1 text-sm text-neutral-500 outline outline-0 outline-offset-0 outline-neutral-300/80 transition-all hover:bg-neutral-200 focus:outline-2 focus:outline-offset-2 dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-900;

  &:not(:disabled) {
    @apply cursor-pointer;
  }
  &:disabled {
    @apply bg-neutral-200 dark:bg-neutral-500;
  }

  &.primary {
    @apply border-black/5 bg-neutral-900 text-white outline-neutral-800/80 hover:bg-neutral-700 active:bg-black dark:ring-2 dark:ring-black/40;
    &:disabled {
      @apply bg-neutral-700;
    }
  }
  &.info {
    @apply border-blue-900/10 bg-blue-500 text-white outline-blue-600/80 hover:bg-blue-400 active:bg-blue-600;
    &:disabled {
      @apply bg-blue-400;
    }
  }
  &.success {
    @apply border-emerald-900/10 bg-emerald-400 text-white outline-emerald-400/80 hover:bg-emerald-500 active:bg-emerald-700;
    &:disabled {
      @apply bg-emerald-300;
    }
  }
  &.warning {
    @apply border-amber-900/10 bg-amber-500 text-white outline-amber-400/80 hover:bg-amber-400 active:bg-amber-600;
    &:disabled {
      @apply bg-amber-400;
    }
  }
  &.danger {
    @apply border-red-900/10 bg-red-500 text-white outline-red-400/80 hover:bg-red-400 active:bg-red-600;
    &:disabled {
      @apply bg-red-400;
    }
  }
  &.white {
    @apply border-neutral-900/10 bg-white text-black hover:bg-neutral-100 active:bg-neutral-200 
    dark:bg-neutral-600 dark:hover:bg-neutral-800 dark:text-neutral-100;
    &:disabled {
      @apply bg-neutral-200 text-neutral-400;
    }
  }
  &.secondary {
    @apply border-zinc-700/10 bg-zinc-500/80 text-white hover:bg-zinc-500 active:bg-zinc-600;
    &:disabled {
      @apply bg-zinc-400;
    }
  }

  &.--pe-button-small {
    @apply p-0 px-2 leading-5;
  }

  &.--pe-button-large {
    @apply p-0 px-4 leading-10;
  }
}

/* Adiciona espaçamento automatico sempre que existe um botão ao lado do outro */
:not(.pe-button-group) {
  > .pe-button + .pe-button {
    @apply ml-4;
  }
}
</style>
